import { useWorkspacePanes } from "./workspace-panes";
import { useLingui } from "@lingui/react/macro";
import type { Bot } from "@rakazo/contracts";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@rakazo/ui-web";
import { Bot as BotIcon, Folder, ShoppingBag, MessagesSquare, Plus, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { IconTooltip } from "../../components/IconTooltip";
import { desktopBridge } from "../../lib/desktop";
import type { Panel } from "../../lib/right-panel-state";
import { WindowChrome } from "../WindowChrome";
import { BotCreatePicker } from "./bot-picker";
import { isCreateMenuHotkey } from "./command-palette-hotkey";

/**
 * Whether the create menu is open, opened by Cmd/Ctrl-N. The shell holds it from its first render,
 * so the shortcut is not lost while the sidebar is still waiting for its first data.
 */
export function useCreateMenu() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (!isCreateMenuHotkey(event)) return;
      event.preventDefault();
      setOpen(true);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return [open, setOpen] as const;
}

export function SidebarHeader({
  mini = false,
  vertical = false,
  createMenuOpen,
  setCreateMenuOpen,
  activityMode,
  searchOpen = false,
  onSearch,
  bots,
  setMobileSidebarOpen,
  setPanel,
  navigate,
  setNewSpaceOpen,
  setCreateBotOpen,
}: {
  mini?: boolean;
  vertical?: boolean;
  createMenuOpen: boolean;
  setCreateMenuOpen: (open: boolean) => void;
  activityMode: boolean;
  searchOpen?: boolean;
  onSearch?: () => void;
  bots: Bot[];
  setMobileSidebarOpen: (open: boolean) => void;
  setPanel: (panel: Panel) => void;
  navigate: (path: string) => void;
  setNewSpaceOpen: (open: boolean) => void;
  setCreateBotOpen: (open: boolean) => void;
}) {
  const { t } = useLingui();
  const panes = useWorkspacePanes();
  const selected = (section: string) => panes?.sidebarView === section;
  const navClass = (section: string) =>
    `app-no-drag flex ${vertical ? "size-11 rounded-xl" : "h-7 w-7 rounded-full"} items-center justify-center ${selected(section) ? "bg-primary text-primary-foreground dark:bg-sidebar-active dark:hover:bg-sidebar-active dark:text-muted-foreground/70 dark:hover:text-muted-foreground/70" : "text-muted-foreground/70 hover:text-foreground/75 dark:hover:text-muted-foreground/70"}`;
  const hasWindowChrome = Boolean(desktopBridge());
  return (
    <div
      className={`app-drag flex flex-col ${vertical ? "items-center gap-2 p-0" : mini ? `items-center gap-2 px-0 pb-2 ${hasWindowChrome ? "pt-4" : "pt-2"}` : "items-stretch gap-3 px-2.5 pb-3 pt-3"}`}
    >
      {hasWindowChrome ? <WindowChrome /> : null}
      <div
        data-testid="workspace-icon-row"
        className={`relative flex items-center ${vertical ? "flex-col gap-3" : mini ? "flex-col gap-0.5" : "w-full flex-wrap justify-between gap-y-1"}`}
      >
        <IconTooltip label={t`Bots`}>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t`Bots`}
            aria-pressed={panes ? selected("bots") : !activityMode && !searchOpen}
            data-testid="bots-shortcut"
            onClick={() => {
              if (panes) panes.selectView("bots");
              else navigate("/app");
            }}
            className={navClass("bots")}
          >
            <BotIcon
              size={vertical ? 21 : 15}
              className={vertical ? "size-5" : undefined}
              strokeWidth={1.8}
              aria-hidden="true"
            />
          </Button>
        </IconTooltip>
        <IconTooltip label="Channels">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Channels"
            aria-pressed={selected("channels")}
            onClick={() => {
              setMobileSidebarOpen(false);
              if (panes) panes.selectView("channels");
              else navigate("/app/channels");
            }}
            className={navClass("channels")}
          >
            <MessagesSquare
              size={vertical ? 21 : 15}
              className={vertical ? "size-5" : undefined}
              strokeWidth={1.8}
              aria-hidden="true"
            />
          </Button>
        </IconTooltip>
        <IconTooltip label={t`Library`}>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t`Library`}
            aria-pressed={selected("library")}
            data-testid="library-shortcut"
            onClick={() => {
              setMobileSidebarOpen(false);
              if (panes) panes.selectView("library");
              else navigate("/app/artifacts");
            }}
            className={navClass("library")}
          >
            <Folder
              size={vertical ? 21 : 15}
              className={vertical ? "size-5" : undefined}
              strokeWidth={1.8}
              aria-hidden="true"
            />
          </Button>
        </IconTooltip>
        <IconTooltip label="Shopping">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Shopping"
            aria-pressed={selected("shopping")}
            data-testid="shopping-shortcut"
            onClick={() => {
              setMobileSidebarOpen(false);
              if (panes) panes.selectView("shopping");
              else navigate("/app/shopping");
            }}
            className={navClass("shopping")}
          >
            <ShoppingBag
              size={vertical ? 21 : 15}
              className={vertical ? "size-5" : undefined}
              strokeWidth={1.8}
              aria-hidden="true"
            />
          </Button>
        </IconTooltip>
        {onSearch ? (
          <IconTooltip label={t`Search`}>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t`Search`}
              aria-pressed={searchOpen}
              data-testid="sidebar-search-trigger"
              onClick={onSearch}
              className={navClass("search")}
            >
              <Search
                size={vertical ? 21 : 15}
                className={vertical ? "size-5" : undefined}
                strokeWidth={1.8}
                aria-hidden="true"
              />
            </Button>
          </IconTooltip>
        ) : null}
        <Popover open={createMenuOpen} onOpenChange={setCreateMenuOpen}>
          <IconTooltip label={t`Add`}>
            <PopoverTrigger
              className={`app-no-drag flex ${vertical ? "size-11 rounded-xl" : "size-7 rounded-full"} items-center justify-center text-muted-foreground/70 outline-none hover:text-foreground/75 dark:hover:text-muted-foreground/70 focus-visible:ring-2 focus-visible:ring-ring`}
              aria-label={t`Add`}
              data-testid="create-menu-trigger"
            >
              <Plus
                size={vertical ? 21 : 15}
                className={vertical ? "size-5" : undefined}
                strokeWidth={1.8}
                aria-hidden="true"
              />
            </PopoverTrigger>
          </IconTooltip>
          {/* Unmount with the state change so the panel it opens never coexists with the menu. */}
          {createMenuOpen ? (
            <PopoverContent
              side={vertical ? "inline-end" : "bottom"}
              align={vertical ? "start" : "end"}
              className="app-no-drag w-auto gap-0 overflow-hidden p-0 data-closed:animate-none"
            >
              <BotCreatePicker
                bots={bots}
                onCreateBot={() => {
                  setCreateMenuOpen(false);
                  setMobileSidebarOpen(false);
                  setCreateBotOpen(true);
                }}
                onOpenBot={(id) => {
                  setCreateMenuOpen(false);
                  setMobileSidebarOpen(false);
                  navigate(`/app/${id}`);
                }}
                onCreateGroup={() => {
                  setCreateMenuOpen(false);
                  setMobileSidebarOpen(false);
                  setPanel("create-group");
                }}
                onCreateSpace={() => {
                  setCreateMenuOpen(false);
                  setMobileSidebarOpen(false);
                  setNewSpaceOpen(true);
                }}
              />
            </PopoverContent>
          ) : null}
        </Popover>
      </div>
    </div>
  );
}
